import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiAward, FiAlertCircle } from "react-icons/fi";
import api from "../../utils/api";

const LeaderBoard = () => {
  const navigate = useNavigate();

  // ✅ STATES
  const [topStudents, setTopStudents] = useState([]);
  const [poorStudents, setPoorStudents] = useState([]);
  const [activeTab, setActiveTab] = useState("top"); // top | poor
  const [loading, setLoading] = useState(false);

  // ✅ API CALL
  async function topper() {
    try {
      setLoading(true);

      const loggedInUser = localStorage.getItem("loggedInUser");
      const token = localStorage.getItem("authToken");
      const programType = "overallTopAndPoorStudents";

      const fd = new FormData();
      fd.append("loggedInUser", loggedInUser);
      fd.append("token", token);
      fd.append("programType", programType);
      fd.append("topLimit", 1000);
      fd.append("poorLimit", 1000);

      const response = await api.post("overView/overView", fd);
      console.log("Topper Data:", response.data);

      if (response?.data?.data) {
        setTopStudents(response.data.data.topStudents || []);
        setPoorStudents(response.data.data.poorStudents || []);
      }
    } catch (error) {
      console.error("Error fetching leaderboard:", error);
    } finally {
      setLoading(false);
    }
  }

  // ✅ LOAD ON PAGE OPEN
  useEffect(() => {
    topper();
  }, []);

  // ✅ FALLBACK PERCENTAGE
  const calculatePercentage = (student) => {
    if (!student?.total_questions || student.total_questions === 0) return 0;
    return Math.round((student.total_correct / student.total_questions) * 100);
  };

  return (
    <div className="dashboard-container">
      {/* ✅ HEADER - Matched AdminDashboard styling */}
      <div className="dashboard-header">
        <div className="header-left">
          <h1>📊 LeaderBoard</h1>
          <p>Top & Weak Student Performance</p>
        </div>

        <div className="header-right">
          {/* ✅ TAB SWITCH - Matched AdminDashboard styling */}
          <div className="card-actions">
            <button
              className={`tab-btn ${activeTab === "top" ? "active" : ""}`}
              onClick={() => setActiveTab("top")}
            >
              Top to Bottom
            </button>
            <button
              className={`tab-btn ${activeTab === "poor" ? "active" : ""}`}
              onClick={() => setActiveTab("poor")}
            >
              Weak Performers
            </button>
          </div>
        </div>
      </div>

      {/* ✅ LOADING */}
      {loading && (
        <div className="loading-small">
          <h3>Loading Leaderboard...</h3>
        </div>
      )}

      {/* ✅ MAIN CONTENT */}
      <div className="dashboard-grid">
        {/* ✅ TOP STUDENTS */}
        {!loading && (
          <div className="dashboard-card performance-card">
            <div className="card-content" style={{ padding: 0 }}>
              {activeTab === "top" ? (
                <div className="performance-table-container">
                  {topStudents.length > 0 ? (
                    <table className="performance-table">
                      <thead>
                        <tr>
                          <th>Rank</th>
                          <th>Student Name</th>
                          <th>Class</th>
                          <th>Accuracy</th>
                          <th>Avg Marks</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topStudents.map((student, index) => (
                          <tr key={index}>
                            <td>
                              <div className="student-rank">{index + 1}</div>
                            </td>
                            <td>
                              <div className="student-name">
                                {student.studentName}
                              </div>
                            </td>
                            <td>{student.className}</td>
                            <td>
                              <div className="accuracy-badge">
                                {student.overall_accuracy ||
                                  calculatePercentage(student)}
                                %
                              </div>
                            </td>
                            <td>
                              <div className="marks-display">
                                {student.total_marks}
                              </div>
                            </td>
                            <td>
                              <button
                                className="view-details-btn"
                                onClick={() =>
                                  navigate(
                                    `/student-overall/${student.student_id}`
                                  )
                                }
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="empty-state">
                      <FiAward size={24} />
                      <p>No top performers data available</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="performance-table-container">
                  {poorStudents.length > 0 ? (
                    <table className="performance-table">
                      <thead>
                        <tr>
                          <th>Rank</th>
                          <th>Student Name</th>
                          <th>Class</th>
                          <th>Accuracy</th>
                          <th>Marks</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {poorStudents.map((student, index) => (
                          <tr key={index}>
                            <td>
                              <div className="student-rank poor-rank">
                                {index + 1}
                              </div>
                            </td>
                            <td>
                              <div className="student-name">
                                {student.studentName}
                              </div>
                            </td>
                            <td>{student.className}</td>
                            <td>
                              <div className="accuracy-badge poor">
                                {student.overall_accuracy ||
                                  calculatePercentage(student)}
                                %
                              </div>
                            </td>
                            <td>
                              <div className="marks-display poor">
                                {student.total_marks}
                              </div>
                            </td>
                            <td>
                              <button
                                className="view-details-btn"
                                onClick={() =>
                                  navigate(
                                    `/student-overall/${student.student_id}`
                                  )
                                }
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="empty-state">
                      <FiAlertCircle size={24} />
                      <p>No weak performers data available</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ✅ ADD CSS STYLES TO MATCH ADMIN DASHBOARD */}
      <style jsx>{`
        .dashboard-container {
          padding: 24px;
          background: #f8fafc;
          min-height: 100vh;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
            sans-serif;
        }

        .dashboard-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          background: white;
          padding: 20px 24px;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .header-left h1 {
          font-size: 24px;
          font-weight: 700;
          color: #1f2937;
          margin: 0;
        }

        .header-left p {
          color: #6b7280;
          margin: 4px 0 0;
          font-size: 14px;
        }

        .header-right {
          display: flex;
          gap: 16px;
          align-items: center;
        }

        .card-actions {
          display: flex;
          gap: 8px;
        }

        .card-actions button {
          padding: 8px 16px;
          border: 1px solid #e5e7eb;
          background: white;
          border-radius: 6px;
          font-size: 14px;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.2s;
          font-weight: 500;
        }

        .card-actions button.active {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }

        .card-actions button:hover:not(.active) {
          background: #f9fafb;
        }

        .dashboard-grid {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .dashboard-card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        .performance-card .card-content {
          padding: 0;
        }

        .performance-table-container {
          overflow-x: auto;
        }

        .performance-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.85rem;
        }

        .performance-table thead {
          background-color: #f9fafb;
        }

        .performance-table th {
          padding: 12px 16px;
          text-align: left;
          font-weight: 600;
          color: #374151;
          border-bottom: 2px solid #e5e7eb;
        }

        .performance-table td {
          padding: 12px 16px;
          border-bottom: 1px solid #f3f4f6;
          vertical-align: middle;
        }

        .performance-table tbody tr:hover {
          background-color: #f9fafb;
        }

        .student-rank {
          width: 28px;
          height: 28px;
          background: #3b82f6;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 12px;
        }

        .student-rank.poor-rank {
          background: #ef4444;
        }

        .student-name {
          font-weight: 600;
          color: #374151;
          font-size: 0.9rem;
        }

        .accuracy-badge {
          display: inline-block;
          padding: 4px 10px;
          background: #d1fae5;
          color: #065f46;
          border-radius: 12px;
          font-weight: 600;
          font-size: 0.8rem;
          min-width: 60px;
          text-align: center;
        }

        .accuracy-badge.poor {
          background: #fee2e2;
          color: #991b1b;
        }

        .marks-display {
          font-weight: 700;
          color: #1f2937;
          font-size: 0.9rem;
        }

        .marks-display.poor {
          color: #ef4444;
        }

        .view-details-btn {
          padding: 6px 12px;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          min-width: 60px;
        }

        .view-details-btn:hover {
          background: #2563eb;
          transform: translateY(-1px);
        }

        .loading-small,
        .empty-state {
          text-align: center;
          padding: 40px;
          color: #6b7280;
          font-size: 14px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .loading-small h3 {
          margin: 0;
          font-weight: 500;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .dashboard-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
          }

          .header-right {
            width: 100%;
            justify-content: flex-start;
          }

          .card-actions {
            width: 100%;
            justify-content: center;
          }

          .performance-table {
            font-size: 0.75rem;
          }

          .performance-table th,
          .performance-table td {
            padding: 8px 12px;
          }
        }

        @media (max-width: 576px) {
          .dashboard-container {
            padding: 16px;
          }

          .card-actions {
            flex-direction: column;
          }

          .card-actions button {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default LeaderBoard;